import React from 'react';
import { observer } from 'mobx-react-lite';
import { ThemeProvider } from 'styled-components';
import { Switch, Route } from 'react-router-dom';

import {
  IndicatorProvider,
  Indicator,
  IndicatorFallback,
  AuthRoute,
  PrivateRoute,
  CandidatePublicRoute,
  CandidatePrivateRoute,
  ScrollUp
} from '@src/features/common';
import { ROUTES } from 'src/constants';
import { basicTheme } from 'src/theme';
import { CookiePolicyBar } from 'src/components';
import { useStore } from './models';

const AuthRoutes = React.lazy(() => import('./pages/Auth'));
const EmployerRoutes = React.lazy(() => import('./pages/Employer'));
const CandidateRoutes = React.lazy(() => import('./pages/Candidate'));
const CandidatePublicRoutes = React.lazy(() => import('./pages/CandidatePublic')); // probably move this under smth like Public dir
const JobRoute = React.lazy(() => import('./pages/CandidatePublic/Job'));
const FreelancerRoutes = React.lazy(() => import('./pages/Freelancer'));
const UserProfile = React.lazy(() => import('./pages/UserProfile')); // will be moved
const ArticleDetail = React.lazy(() => import('./features/feed/organisms/ArticleDetail'));

const RootRoutes = observer(() => {
  const { routerStore } = useStore();
  return (
    <>
      <ThemeProvider theme={basicTheme}>
        <IndicatorProvider>
          <Indicator />
          <React.Suspense fallback={<IndicatorFallback />}>
            <Switch>
              <AuthRoute path={ROUTES.auth} component={AuthRoutes} />
              <PrivateRoute path={ROUTES.profile.base} component={UserProfile} />
              {routerStore.isCompanyRoute && (
                <PrivateRoute
                  path={[
                    ROUTES.employer.dashboard,
                    ROUTES.employer.talentPool.base,
                    ROUTES.employer.talentPool.applicant,
                    ROUTES.employer.companyProfile.base,
                    ROUTES.employer.preferences.hiring,
                    ROUTES.employer.preferences.emailTemplates,
                    ROUTES.employer.preferences.career,
                    ROUTES.employer.preferences.tags,
                    ROUTES.employer.jobs.application,
                    ROUTES.employer.jobs.suggestions,
                    ROUTES.employer.jobs.advertising,
                    ROUTES.employer.jobs.feeds,
                    ROUTES.employer.jobs.members,
                    ROUTES.employer.jobs.new,
                    ROUTES.employer.jobs.edit,
                    ROUTES.employer.jobs.clone,
                    ROUTES.employer.jobs.applicants.base,
                    ROUTES.employer.jobs.applicants.applicant,
                    ROUTES.employer.candidatesSearch,
                    ROUTES.employer.documents,
                    ROUTES.employer.inactive
                  ]}
                  component={EmployerRoutes}
                />
              )}
              <PrivateRoute
                path={[
                  ROUTES.freelancers.base,
                  ROUTES.freelancers.jobs.job.applicant,
                  ROUTES.freelancers.talentPool.base,
                  ROUTES.freelancers.candidatesSearch,
                  ROUTES.freelancers.talentPool.applicant
                ]}
                component={FreelancerRoutes}
              />
              <Route path={ROUTES.jobs.job} exact component={JobRoute} />
              <CandidatePublicRoute
                path={[
                  ROUTES.magazine,
                  ROUTES.resources,
                  ROUTES.candidates,
                  ROUTES.resources + '/:id',
                  ROUTES.articles,
                  ROUTES.articles + '/:id',
                  ROUTES.jobs.base,
                  ROUTES.jobs.applications.application
                ]}
                exact
                component={CandidatePublicRoutes}
              />
              <CandidatePrivateRoute path={ROUTES.candidatesRoutes.base} component={CandidateRoutes} />
            </Switch>
          </React.Suspense>
          <CookiePolicyBar />
        </IndicatorProvider>
      </ThemeProvider>
      <ScrollUp />
    </>
  );
});

export default RootRoutes;
